import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from '@phosphor-icons/react';
import { applySeo } from '../lib/seo';
import { BlogPdfBox } from '../components/BlogPdfBox';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// /blog — public index of the "Your CRM Should Work For You" essay series.
export default function BlogPage() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/blog`).then(({ data }) => setPosts(data)).catch(() => setPosts([]));
    return applySeo({
      title: 'The SalesCrunch Blog — Essays from the anti-Salesforce side of CRM',
      description:
        'No pipelines. No lead scores. No stage gates. Essays on why CRM software stopped working for salespeople — and what we built instead.',
      path: '/blog',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="blog-page">
      <header className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-black text-slate-900 hover:text-[#0B5394]" data-testid="blog-home-link">
            SalesCrunch<span className="text-[#0B5394]">CRM</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-4 py-2 rounded"
            data-testid="blog-top-cta"
          >
            Try it — $30/user/mo
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#CC6600] mb-3">The SalesCrunch Blog</p>
        <h1 className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight mb-4" data-testid="blog-title">
          Essays from the anti-Salesforce side of the CRM industry
        </h1>
        <p className="text-lg text-slate-500 mb-4">
          No pipelines. No lead scores. No stage gates. Just what the prospect told you &mdash; and software that listens.
        </p>
        <Link to="/manifesto" className="text-sm font-bold text-[#0B5394] hover:underline" data-testid="blog-manifesto-link">
          Start with the Anti-Salesforce Manifesto &rarr;
        </Link>

        <div className="mt-12 divide-y divide-slate-200" data-testid="blog-post-list">
          {posts === null && <p className="py-10 text-slate-400">Loading&hellip;</p>}
          {posts !== null && posts.length === 0 && <p className="py-10 text-slate-400">No posts yet.</p>}
          {(posts || []).map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group block py-8"
              data-testid={`blog-post-link-${p.slug}`}
            >
              <p className="text-xs font-bold uppercase tracking-wider text-[#CC6600] mb-2">
                {p.series} &middot; {fmtDate(p.date)} &middot; {p.read_minutes} min read
              </p>
              <h2 className="text-lg md:text-lg font-black text-slate-900 group-hover:text-[#0B5394] leading-snug mb-2">
                {p.title}
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed mb-3">{p.description}</p>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0B5394]">
                Read the essay <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-4 mb-8">
          <BlogPdfBox />
        </div>

        <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-8 text-center" data-testid="blog-cta-card">
          <p className="text-xl font-black text-slate-900 mb-2">Remember everything. Miss nothing. No training required.</p>
          <p className="text-sm text-slate-500 mb-5">SalesCrunchCRM is $30 per user per month. MigrationCrunch moves your old CRM in — notes and all.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/signup" className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-5 py-2.5 rounded" data-testid="blog-cta-crm">
              Try SalesCrunchCRM
            </Link>
            <Link to="/migrate" className="text-sm font-bold text-[#0B5394] border border-[#0B5394] hover:bg-blue-50 px-5 py-2.5 rounded" data-testid="blog-cta-migrate">
              Migrate free — first 50 contacts
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
